import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import DistributionHubService from 'service/DistributionHubService';
import CustomDropdown from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';
import DistributionHubDetailModal from 'component/DistributionHubDetailModal';
import { hasDistributionHubAccess } from 'helpers/hasPrivilege';
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';
import Map, { Marker, MapRef, NavigationControl } from 'react-map-gl/mapbox';
import { MAPBOX_TOKEN } from 'config/Mapbox';
import { DistributionHubGeoListResp } from 'domain/payload/response/distributionHub/DistributionHubGeoListResp';

const DistributionHub: React.FC = () => {
    const { profile } = useSelector((state: any) => state.auth);
    const token = profile?.data?.token;
    const [data, setData] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selectedHubId, setSelectedHubId] = useState<string | null>(null);

    // Area filter state
    const [selectedAreaId, setSelectedAreaId] = useState<string>('');
    const [availableAreas, setAvailableAreas] = useState<{ id: string; name: string }[]>([]);

    // Accumulate unique areas from data
    useEffect(() => {
        if (data && data.length > 0) {
            setAvailableAreas((prev) => {
                const areaDict: Record<string, string> = {};
                prev.forEach((a) => {
                    areaDict[a.id] = a.name;
                });
                data.forEach((item) => {
                    if (item.areaId && item.areaName) {
                        areaDict[String(item.areaId)] = item.areaName;
                    }
                });
                return Object.keys(areaDict).map((id) => ({
                    id,
                    name: areaDict[id],
                }));
            });
        }
    }, [data]);

    const areaOptions = useMemo(() => {
        return availableAreas.map((a) => ({
            id: a.id,
            name: a.name,
        }));
    }, [availableAreas]);

    const displayedData = useMemo(() => {
        if (!selectedAreaId) return data;
        return data.filter((item) => String(item.areaId) === String(selectedAreaId));
    }, [data, selectedAreaId]);

    // View mode state (List vs Map)
    const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
    const { navRef: viewModeNavRef, indicatorStyle: viewModeIndicatorStyle } = useNavPillsIndicator(viewMode);

    // Geo list state
    const [geoData, setGeoData] = useState<DistributionHubGeoListResp[]>([]);
    const [geoLoading, setGeoLoading] = useState(false);
    const mapRef = useRef<MapRef | null>(null);

    const distributionHubService = DistributionHubService();

    const canCreate = hasDistributionHubAccess(profile, 'create');
    const canUpdate = hasDistributionHubAccess(profile, 'update');
    const canDelete = hasDistributionHubAccess(profile, 'delete');

    const fetchData = async () => {
        const token = profile?.data?.token;
        if (!token) {
            window.alert('Token tidak ditemukan. Silakan login kembali.');
            return;
        }

        setLoading(true);
        try {
            const res = await distributionHubService.distributionHubList(token, appliedSearchTerm, currentPage, pageSize);
            setData(res.data ?? []);
            setTotalPages(res.pagination?.totalPage ?? 0);
            setTotalRecords(res.pagination?.totalRecords ?? 0);
        } catch (err: any) {
            console.error(err);
            const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data Distribution Hub';
            window.alert(`Error: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    };

    const fetchGeoData = useCallback(async () => {
        const token = profile?.data?.token;
        if (!token) return;

        setGeoLoading(true);
        try {
            const res = await distributionHubService.distributionHubGeoList(token);
            setGeoData(res.data ?? []);
        } catch (err: any) {
            console.error('Error fetching distribution hub geo list:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data titik peta Distribution Hub';
            window.alert(`Error: ${errorMessage}`);
        } finally {
            setGeoLoading(false);
        }
    }, [profile]);

    useEffect(() => {
        if (viewMode === 'list') {
            fetchData();
        } else {
            fetchGeoData();
        }
    }, [currentPage, pageSize, appliedSearchTerm, viewMode]);

    // Resize map canvas when side modal opens or closes
    useEffect(() => {
        if (viewMode === 'map') {
            const timer = setTimeout(() => {
                mapRef.current?.resize();
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [selectedHubId, viewMode]);

    const handleMapLoad = useCallback(() => {
        if (!mapRef.current) return;
        const validPoints = geoData.filter((item) => item.latitude && item.longitude);
        if (validPoints.length === 0) return;

        const lngs = validPoints.map((item) => item.longitude);
        const lats = validPoints.map((item) => item.latitude);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);

        if (minLng === maxLng && minLat === maxLat) {
            mapRef.current.flyTo({
                center: [minLng, minLat],
                zoom: 12,
                duration: 600,
            });
        } else {
            mapRef.current.fitBounds(
                [
                    [minLng, minLat],
                    [maxLng, maxLat],
                ],
                { padding: 80, duration: 800 }
            );
        }
    }, [geoData]);

    useEffect(() => {
        if (viewMode === 'map' && geoData.length > 0) {
            handleMapLoad();
        }
    }, [viewMode, geoData, handleMapLoad]);

    const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setCurrentPage(1);
        setAppliedSearchTerm(searchTerm);
    };

    const handleDelete = async (id: string) => {
        if (!token) {
            window.alert('Token tidak ditemukan. Silakan login kembali.');
            return;
        }
        if (!window.confirm("Apakah Anda yakin ingin menghapus Distribution Hub ini?")) return;
        try {
            setLoading(true);
            await distributionHubService.distributionHubDelete(token, id);
            window.alert('Distribution Hub berhasil dihapus');
            fetchData();
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Terjadi kesalahan saat menghapus Distribution Hub';
            window.alert(`Error: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="page-wrapper">
            <div className="row">
                <div className="col-12">
                    <div className="d-flex">
                        <h5 style={{ flex: 1 }}>Distribution Hub</h5>
                        <div>
                            <p>TransMore Distribution Hub</p>
                        </div>
                    </div>
                </div>
                <div className="col-12">
                    <div className="p-4" style={{ background: 'white' }}>
                        <div className="d-flex mb-3 flex-wrap align-items-center gap-2">
                            <div className="col-auto">
                                <CustomDropdown
                                    value={selectedAreaId || null}
                                    options={areaOptions}
                                    onChange={(opt) => setSelectedAreaId(opt.id ? String(opt.id) : '')}
                                    placeholder="All Area"
                                    showDefaultOption={true}
                                    defaultOptionLabel="All Area"
                                    searchable={areaOptions.length > 5}
                                    searchPlaceholder="Search area..."
                                    containerStyle={{ width: '180px' }}
                                    buttonClassName="btn btn-outline-secondary d-flex align-items-center justify-content-between gap-2 px-3 py-2 text-dark bg-white border rounded-3 w-100"
                                />
                            </div>

                            {/* Nav Pills: List View / Map View */}
                            <div ref={viewModeNavRef} className="nav-pills-animated">
                                <div className="nav-pill-indicator" style={viewModeIndicatorStyle} />
                                <button
                                    type="button"
                                    className={`nav-link ${viewMode === 'list' ? 'active' : ''}`}
                                    onClick={() => {
                                        setViewMode('list');
                                        setSelectedHubId(null);
                                    }}
                                >
                                    List View
                                </button>
                                <button
                                    type="button"
                                    className={`nav-link ${viewMode === 'map' ? 'active' : ''}`}
                                    onClick={() => {
                                        setViewMode('map');
                                        setSelectedHubId(null);
                                    }}
                                >
                                    Map View
                                </button>
                            </div>

                            <form className="d-flex" onSubmit={handleSearchClick}>
                                <input
                                    type="text"
                                    className="form-control form-control-compact me-2"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ minWidth: 160 }}
                                />
                                <button type="submit" className="btn btn-primary px-4">
                                    <i className="fa-solid fa-search"></i>
                                </button>
                            </form>

                            <div className="d-flex justify-content-end ms-auto">
                                {canCreate && (
                                    <Link to="/distribution-hub/0" className='float-end text-decoration-none tm-text-primary'>
                                        <button className="btn btn-primary">
                                            <span className="me-2">
                                                Add
                                            </span>
                                            <i className="fa-solid fa-plus"></i>
                                        </button>
                                    </Link>
                                )}
                            </div>
                        </div>

                        <div className="row g-4">
                            <div className={selectedHubId ? "col-12 col-xl-7" : "col-12"}>
                                {viewMode === 'list' ? (
                                    <>
                                        <div className="table-responsive">
                                            <table className="table table-condensed table-hover align-middle">
                                                <thead className="table-primary">
                                                    <tr>
                                                        <th>ID</th>
                                                        <th>Name</th>
                                                        <th>PIC Name</th>
                                                        <th>PIC Email</th>
                                                        <th>Area</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {!loading ? (
                                                        displayedData.length > 0 ? (
                                                            displayedData.map((item) => (
                                                                <tr
                                                                    key={item.id}
                                                                    className={selectedHubId === item.id ? 'table-secondary' : ''}
                                                                >
                                                                    <td style={{ fontSize: '13px' }}>{item.id}</td>
                                                                    <td className="fw-semibold" style={{ fontSize: '13px' }}>{item.name || '-'}</td>
                                                                    <td style={{ fontSize: '13px' }}>{item.picName || '-'}</td>
                                                                    <td style={{ fontSize: '13px' }}>{item.picEmail || '-'}</td>
                                                                    <td style={{ fontSize: '13px' }}>{item.areaName || '-'}</td>
                                                                    <td>
                                                                        <div className="d-flex" onClick={(e) => e.stopPropagation()}>
                                                                            <button
                                                                                className={`border-0 btn btn-small-compact d-flex align-items-center justify-content-center ${selectedHubId === item.id ? 'btn-info text-white' : 'btn-outline-info'}`}
                                                                                onClick={() => setSelectedHubId(selectedHubId === item.id ? null : item.id)}
                                                                                title="View Details"
                                                                                style={{ width: '32px', height: '32px' }}
                                                                            >
                                                                                <i className="fa-solid fa-eye"></i>
                                                                            </button>

                                                                            {canUpdate && (
                                                                                <Link to={`/distribution-hub/${item.id}`}>
                                                                                    <button className="border-0 btn btn-outline-warning btn-small-compact ms-2">
                                                                                        <i className="fa-solid fa-edit"></i>
                                                                                    </button>
                                                                                </Link>
                                                                            )}

                                                                            {canDelete && (
                                                                                <button className="border-0 btn btn-outline-danger btn-small-compact ms-2" onClick={() => handleDelete(item.id)}>
                                                                                    <i className="fa-solid fa-trash"></i>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={6} className="text-center py-4">
                                                                    Tidak ada data
                                                                </td>
                                                            </tr>
                                                        )
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={6} className="text-center py-4">
                                                                <div className="spinner-border text-primary" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="d-flex justify-content-end align-items-center mt-3">
                                            <div className="d-flex align-items-center">
                                                <div className="me-4 d-flex align-items-center">
                                                    <label className="me-2">Rows per page:</label>
                                                    <CustomDropdown
                                                        value={pageSize}
                                                        options={PageSizeOption.options}
                                                        onChange={(opt) => {
                                                            setPageSize(Number(opt.id));
                                                            setCurrentPage(1);
                                                        }}
                                                        align="top"
                                                    />
                                                </div>

                                                <span className="me-3">
                                                    {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
                                                </span>

                                                <nav>
                                                    <ul className="pagination mb-0">
                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                            <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                                                                <i className="fa fa-angle-double-left"></i>
                                                            </button>
                                                        </li>
                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                            <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                                                                <i className="fa fa-angle-left"></i>
                                                            </button>
                                                        </li>

                                                        <li className="page-item disabled">
                                                            <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                                                        </li>

                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                            <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                                                                <i className="fa fa-angle-right"></i>
                                                            </button>
                                                        </li>
                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                            <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                                                                <i className="fa fa-angle-double-right"></i>
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div
                                        className="position-relative w-100 rounded-3 overflow-hidden border"
                                        style={{
                                            height: 'calc(100vh - 270px)',
                                            minHeight: '540px',
                                            borderColor: '#e5e7eb'
                                        }}
                                    >
                                        {geoLoading && (
                                            <div
                                                className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
                                                style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 10 }}
                                            >
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <p className="text-muted small mt-2 mb-0">Memuat titik peta...</p>
                                            </div>
                                        )}

                                        <Map
                                            ref={mapRef}
                                            mapboxAccessToken={MAPBOX_TOKEN}
                                            initialViewState={{
                                                latitude: -1.5,
                                                longitude: 115.5,
                                                zoom: 4.5,
                                            }}
                                            style={{ width: '100%', height: '100%' }}
                                            mapStyle="mapbox://styles/mapbox/streets-v12"
                                            onLoad={handleMapLoad}
                                        >
                                            <NavigationControl position="bottom-right" />
                                            {geoData.map((item) => (
                                                item.latitude && item.longitude ? (
                                                    <Marker
                                                        key={item.id}
                                                        latitude={item.latitude}
                                                        longitude={item.longitude}
                                                        anchor="bottom"
                                                        onClick={(e) => {
                                                            e.originalEvent.stopPropagation();
                                                            setSelectedHubId(item.id);
                                                            mapRef.current?.flyTo({
                                                                center: [item.longitude, item.latitude],
                                                                zoom: Math.max(mapRef.current.getZoom(), 8),
                                                                duration: 600,
                                                            });
                                                        }}
                                                    >
                                                        <div
                                                            className="d-flex flex-column align-items-center"
                                                            style={{ cursor: 'pointer', transform: 'translateY(-10px)' }}
                                                        >
                                                            {/* Pin icon circle */}
                                                            <div
                                                                className="rounded-circle d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    width: '32px',
                                                                    height: '32px',
                                                                    backgroundColor: '#2B75DA',
                                                                    border: '2px solid #ffffff',
                                                                    color: '#ffffff',
                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                                                    transition: 'transform 0.2s ease',
                                                                }}
                                                                title={item.name}
                                                            >
                                                                <i className="fa fa-cubes" style={{ fontSize: '13px' }}></i>
                                                            </div>

                                                            {/* Dark Pill Label */}
                                                            <div
                                                                className="mt-1 px-2 py-0.5 text-white rounded text-center"
                                                                style={{
                                                                    backgroundColor: 'rgba(17, 24, 39, 0.85)',
                                                                    fontSize: '11px',
                                                                    fontWeight: 500,
                                                                    letterSpacing: '0.2px',
                                                                    whiteSpace: 'nowrap',
                                                                    maxWidth: '180px',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)'
                                                                }}
                                                            >
                                                                {item.name}
                                                            </div>
                                                        </div>
                                                    </Marker>
                                                ) : null
                                            ))}
                                        </Map>
                                    </div>
                                )}
                            </div>

                            {selectedHubId && (
                                <div className="col-12 col-xl-5">
                                    <DistributionHubDetailModal
                                        hubId={selectedHubId}
                                        onClose={() => setSelectedHubId(null)}
                                        asModal={false}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DistributionHub;